import { useCallback, useEffect, useMemo, useState } from "react";

import { cloneDeep, isArray, isEqual } from "lodash";
import { useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { withDefault } from "serialize-query-params";
import { type MarkOptional } from "ts-essentials";
import { useQueryParam } from "use-query-params";
import { v4 as uuidv4 } from "uuid";

import { useLogger } from "@components/v2/analytics";
import { usePrevious } from "@hooks/use-previous";
import {
  type AmplitudeEventContainer,
  type AmplitudeNativeEventType,
} from "@north-beam/nb-client-side";
import {
  CustomJsonCompressedParam,
  replaceInArray,
} from "@north-beam/nb-common";

import {
  gridIsReady,
  TileType,
  UndoActionType,
  type Grid,
  type Layouts,
  type OverviewSavedView,
  type TileDescriptor,
  type TileFilter,
  type UndoAction,
} from "../types";
import { initializeTileLayouts } from "../utils";

import { useGrid } from "./use-grid";
import { type ModifyViewsOptions } from "./use-saved-views";

type ExistingTilesCallback<T> = (tiles: TileDescriptor[]) => T;
type ExistingTilesCallbackOrType<T> = T | ExistingTilesCallback<T>;

type TileUpdate =
  | Partial<Omit<TileDescriptor, "id">>
  | ((t: TileDescriptor) => Partial<Omit<TileDescriptor, "id">>);

type UpdateTileParams = {
  id: string;
  update: TileUpdate;
};

type AddNewTileParams = {
  type: TileType;
  metricId: string;
  name: string;
  breakdowns: string[];
  breakdownFilters: string[][];
  filters: TileFilter[];
};

type UpdateTileLayoutParams = {
  layouts: ReactGridLayout.Layout[];
};

type ModifyTilesOptions = {
  readonly successMessage?: string;
  readonly errorMessage?: string;
  readonly onError?: (e: unknown) => void;
  readonly onSuccess?: () => void;
};

type PrivateForwardModifyTilesOptions = ModifyTilesOptions & {
  readonly isUndoing?: never;
  readonly actionType: UndoActionType;
};

type PrivateUndoModifyTilesOptions = ModifyTilesOptions & {
  readonly isUndoing: true;
  readonly actionType?: never;
};

type PrivateModifyTilesOptions =
  | PrivateUndoModifyTilesOptions
  | PrivateForwardModifyTilesOptions;

export interface TilesManager {
  readonly lastActionType: UndoActionType | null;
  readonly tiles: TileDescriptor[];
  readonly grid: Grid;
  readonly notifyUserOfTemplateChange: boolean;
  /**
   * Persists an {@link UnsavedAction} by creating a new view with the unsaved modifications that
   * were made to a template, or default {@link OverviewSavedView}.
   */
  readonly persistUnsavedAction: (
    name: string,
    context: {
      container: AmplitudeEventContainer;
      eventType: AmplitudeNativeEventType;
    },
    options?: ModifyTilesOptions,
  ) => Promise<void>;
  readonly dismissUnsavedAction: () => void;
  readonly deleteTile: (
    tile: TileDescriptor,
    options?: ModifyTilesOptions,
  ) => void;
  readonly cloneTile: (
    tile: TileDescriptor,
    options?: ModifyTilesOptions,
  ) => void;
  readonly addNewTile: (
    params: AddNewTileParams,
    options?: ModifyTilesOptions,
  ) => void;
  readonly updateTileLayout: (
    params: UpdateTileLayoutParams,
    options?: ModifyTilesOptions,
  ) => void;
  readonly updateTiles: (
    params: UpdateTileParams | UpdateTileParams[],
    options?: ModifyTilesOptions,
  ) => void;
  readonly undoLastAction: (options?: ModifyTilesOptions) => void;
}

export const TilesParam = withDefault(
  CustomJsonCompressedParam<any[] | null>(),
  null,
);

type SetTilesSetter =
  | TileDescriptor[]
  | ((
      curr: TileDescriptor[],
      savedView: OverviewSavedView,
    ) => TileDescriptor[]);

export interface UseTilesConfig {
  readonly selectedSavedView: OverviewSavedView | null;
  readonly createSavedView: (
    name: OverviewSavedView["name"],
    tiles: TileDescriptor[],
    context: {
      container: AmplitudeEventContainer;
      eventType: AmplitudeNativeEventType;
    },
    options?: ModifyViewsOptions,
  ) => Promise<void>;
  readonly updateSavedView: (
    id: number,
    tiles: TileDescriptor[],
    options?: ModifyViewsOptions,
  ) => Promise<void>;
}

const compressLayoutsDefintionForUrl = (layouts: Layouts) => {
  const compressed = Object.keys(layouts).reduce((result, key) => {
    const { x, y, width, height } = layouts[key as keyof Layouts];
    result[key as keyof Layouts] = [x, y, width, height];
    return result;
  }, {} as any);
  return compressed;
};
const compressTileFilterDefinitonForUrl = (tileFilter: TileFilter) => {
  return [tileFilter.breakdown, tileFilter.value];
};
const compressTileDefinitionForUrl = (tile: TileDescriptor) => {
  return [
    tile.id,
    tile.metricId,
    tile.metric2Id,
    tile.alignAxes,
    tile.sortBreakdowns,
    tile.name,
    tile.type,
    tile.breakdowns,
    tile.breakdownFilters,
    tile.filters.map(compressTileFilterDefinitonForUrl),
    tile.metric2Filters?.map(compressTileFilterDefinitonForUrl),
    tile.layouts ? compressLayoutsDefintionForUrl(tile.layouts) : tile.layouts,
  ];
};
const hydrateLayouts = (compressedLayouts: any) => {
  return Object.keys(compressedLayouts).reduce((result, key) => {
    const [x, y, width, height] = compressedLayouts[key as keyof Layouts];
    result[key as keyof Layouts] = { x, y, width, height };
    return result;
  }, {} as Layouts);
};
const hydrateFilterDefinition = (compressedFilter: string[]) => {
  return {
    breakdown: compressedFilter[0],
    value: compressedFilter[1],
  };
};
const hydrateTileDefinition = (compressedTile: any[]): TileDescriptor => {
  if (isArray(compressedTile)) {
    return {
      id: compressedTile[0],
      metricId: compressedTile[1],
      metric2Id: compressedTile[2],
      alignAxes: compressedTile[3],
      sortBreakdowns: compressedTile[4],
      name: compressedTile[5],
      type: compressedTile[6],
      breakdowns: compressedTile[7],
      breakdownFilters: compressedTile[8],
      filters: compressedTile[9]?.map(hydrateFilterDefinition),
      metric2Filters: compressedTile[10]?.map(hydrateFilterDefinition),
      layouts: compressedTile[11]
        ? hydrateLayouts(compressedTile[11])
        : compressedTile[11],
    };
  } else {
    return compressedTile;
  }
};

type NewValueType<D> = D | ((latestValue: D) => D);

export const useTiles = ({
  selectedSavedView,
  updateSavedView,
  createSavedView,
}: UseTilesConfig): TilesManager => {
  const logger = useLogger({ container: "tile-management" });

  const [compressedTiles, _setCompressedTiles] = useQueryParam(
    "tiles",
    TilesParam,
    {
      updateType: "pushIn",
    },
  );

  const _setTiles = useCallback(
    (newTiles: NewValueType<TileDescriptor[] | null>) => {
      if (typeof newTiles === "function") {
        return _setCompressedTiles((oldTiles) => {
          const hydrated = oldTiles?.map(hydrateTileDefinition) ?? null;
          const changed = newTiles(hydrated);
          return changed?.map(compressTileDefinitionForUrl) ?? null;
        });
      } else {
        return _setCompressedTiles(
          newTiles?.map(compressTileDefinitionForUrl) ?? null,
        );
      }
    },
    [_setCompressedTiles],
  );
  const tiles = useMemo(() => {
    return compressedTiles?.map(hydrateTileDefinition) ?? null;
  }, [compressedTiles]);

  const grid = useGrid({ tiles });

  const viewId = selectedSavedView ? selectedSavedView.id : null;
  const previousSavedViewId = usePrevious(
    selectedSavedView === null ? null : selectedSavedView.id,
  );
  const params = useParams();

  /* Note: The `lastAction` needs to be stored in state so that the 'lastActionType' attribute
     causes a rerender. */
  const [lastAction, registerAction] = useState<UndoAction | null>(null);
  const [templateViewWasAltered, setTemplateViewWasAltered] = useState(false);
  /* Whether or not the user has been already notified that they are making changes to an unsavable
     view and asked if they would like to create a new view from those changes. */
  const [userWasNotifiedOfTemplate, setUserWasNotifiedOfTemplate] =
    useState(false);

  const { addToast } = useToasts();

  useEffect(() => {
    if (selectedSavedView) {
      _setTiles((tiles) => {
        if (tiles === null) {
          return selectedSavedView.config.pageConfiguration.tiles;
        }
        return tiles;
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [params]);

  useEffect(() => {
    if (
      viewId &&
      previousSavedViewId &&
      viewId !== previousSavedViewId &&
      selectedSavedView !== null
    ) {
      _setTiles(selectedSavedView.config.pageConfiguration.tiles);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [viewId]);

  useEffect(() => {
    /* Clear both the last undoable action, and any potentially unsaved modification to a template
       view, if the saved view is altered. */
    registerAction(null);
    setUserWasNotifiedOfTemplate(false);
    setTemplateViewWasAltered(false);
  }, [selectedSavedView]);

  const setTiles = useCallback(
    async (setter: SetTilesSetter, options: PrivateModifyTilesOptions) => {
      if (selectedSavedView && tiles !== null) {
        const original = [...tiles];

        let updated: TileDescriptor[];
        if (typeof setter === "function") {
          updated = setter(original, selectedSavedView);
        } else {
          updated = setter;
        }

        const write = async (localOptions?: { onSuccess?: () => void }) => {
          localOptions?.onSuccess?.();
          options?.onSuccess?.();
        };
        /* If the view is a default or template view, the user is not allowed to edit it - instead,
           we will temporarily show the tiles as if they have been changed - and prompt the user if
           they would like to create a new view with those changes applied.

           In both cases, the actual state is updated to reflect the changes that were made and
           allow the user to potentially undo the last action they took - the difference is that
           when the view is a template view, the changes will not persist to the database - they
           will only exist in state.

           In both cases, we want to optimistically update the tiles in the URL query parameters
           so that the change is immediately reflected - and then make the API request (if
           applicable) in the background - rolling back changes if there is an error. */

        /* Optimistically update the state to reflect the state before the last change was
           applied. */
        _setTiles(updated);
        if (!isEqual(updated, original)) {
          if (options.isUndoing === true) {
            if (selectedSavedView.is_default) {
              setTemplateViewWasAltered(true);
              registerAction(null);
            } else {
              write({
                onSuccess: () => {
                  // Do not clear the undo action until the request succeeds.
                  registerAction(null);
                },
              });
            }
          } else if (selectedSavedView.is_default) {
            options?.onSuccess?.();
            setTemplateViewWasAltered(true);
            registerAction({
              type: options.actionType,
              previousState: original,
            });
          } else {
            write({
              onSuccess: () =>
                registerAction({
                  type: options.actionType,
                  previousState: original,
                }),
            });
          }
        }
      }
    },
    [selectedSavedView, tiles, _setTiles],
  );

  const undoLastAction = useCallback(
    (options?: ModifyTilesOptions) => {
      if (lastAction) {
        setTiles(lastAction.previousState, { ...options, isUndoing: true });
        registerAction(null);
      }
    },
    [lastAction, setTiles],
  );

  const _addTile = useCallback(
    (
      tile: ExistingTilesCallbackOrType<
        Omit<MarkOptional<TileDescriptor, "breakdowns" | "filters">, "id">
      >,
      options: PrivateForwardModifyTilesOptions,
    ) =>
      setTiles((existing) => {
        const baseTile = typeof tile === "function" ? tile(existing) : tile;
        return [
          ...existing,
          {
            breakdowns: [],
            filters: [],
            ...baseTile,
            id: uuidv4(),
          },
        ];
      }, options),
    [setTiles],
  );

  const updateTiles = useCallback(
    (
      params: UpdateTileParams | UpdateTileParams[],
      options?: ModifyTilesOptions,
    ) => {
      const updates = Array.isArray(params) ? params : [params];
      if (updates.length !== 0) {
        setTiles(
          (existing) =>
            updates.reduce(
              (prev: TileDescriptor[], { update, id }: UpdateTileParams) => {
                const [replaced, newArr] = replaceInArray(prev, {
                  predicate: (t) => t.id === id,
                  insert: (t) => ({
                    ...t,
                    ...(typeof update === "function" ? update(t) : update),
                  }),
                });
                if (!replaced) {
                  /* eslint-disable-next-line no-console -- We need to figure out FE logging. */
                  console.error(
                    `Cannot update tile with ID '${id}' because it does not exist in state!`,
                  );
                }
                return newArr;
              },
              existing,
            ),
          { ...options, actionType: UndoActionType.UPDATE_TILE },
        );
      }
    },
    [setTiles],
  );

  const addNewTile = useCallback(
    (params: AddNewTileParams, options?: ModifyTilesOptions) => {
      _addTile(
        (existing) => ({
          ...params,
          layouts: initializeTileLayouts({
            tiles: existing,
            dimensions: () => ({
              width: 1,
              height: params.type === TileType.METRIC ? 1 : 2,
            }),
          }),
        }),
        { ...options, actionType: UndoActionType.ADD_NEW_TILE },
      );
      logger.event.overviewPageAddTile("click", {
        eventDetails: {
          ...params,
        },
      });
    },
    [_addTile, logger.event],
  );

  const deleteTile = useCallback(
    (tile: TileDescriptor, options?: ModifyTilesOptions) => {
      setTiles(
        (existing) => {
          if (!existing.map((t) => t.id).includes(tile.id)) {
            /* eslint-disable-next-line no-console -- We need to figure out frontend logging. */
            console.error(
              `Cannot delete tile with ID '${tile.id}' as it does not exist in state!`,
            );
            addToast("There was an error deleting the tile.", {
              appearance: "error",
            });
            return existing;
          } else {
            return existing.filter((e) => e.id !== tile.id);
          }
        },
        {
          errorMessage: "There was an error deleting the tile",
          ...options,
          actionType: UndoActionType.DELETE_TILE,
        },
      );
    },
    [addToast, setTiles],
  );

  const cloneTile = useCallback(
    (tile: TileDescriptor, options?: ModifyTilesOptions) =>
      _addTile(
        (existing) => ({
          ...cloneDeep(tile),
          layouts: initializeTileLayouts({
            tiles: existing,
            dimensions: (breakpoint) => ({
              height: tile.layouts[breakpoint].height,
              width: tile.layouts[breakpoint].width,
            }),
          }),
        }),
        {
          errorMessage: "There was an error cloning the tile.",
          ...options,
          actionType: UndoActionType.CLONE_TILE,
        },
      ),
    [_addTile],
  );

  const updateTileLayout = useCallback(
    ({ layouts }: UpdateTileLayoutParams) => {
      if (gridIsReady({ width: grid.width })) {
        updateTiles(
          layouts.map((layoutElement): UpdateTileParams => {
            return {
              id: layoutElement.i,
              update: (t) => {
                const breakpoint = grid.breakpoint;
                const breakpointInfoToUpdate = breakpoint
                  ? {
                      [grid.breakpoint]: {
                        x: layoutElement.x,
                        y: layoutElement.y,
                        width: layoutElement.w,
                        height: layoutElement.h,
                      },
                    }
                  : {};
                return {
                  layouts: {
                    ...t.layouts,
                    ...breakpointInfoToUpdate,
                  },
                };
              },
            };
          }),
        );
      }
    },
    [grid.width, grid.breakpoint, updateTiles],
  );

  return {
    tiles: tiles === null ? [] : tiles,
    grid,
    notifyUserOfTemplateChange:
      selectedSavedView?.is_default === true &&
      !userWasNotifiedOfTemplate &&
      templateViewWasAltered,
    lastActionType: lastAction?.type ?? null,
    dismissUnsavedAction: () => {
      setUserWasNotifiedOfTemplate(true);
    },
    persistUnsavedAction: async (
      name,
      context: {
        container: AmplitudeEventContainer;
        eventType: AmplitudeNativeEventType;
      },
      options?: ModifyTilesOptions,
    ) => {
      /* Note: The unsaved action will be wiped implicitly because when a new saved view is created,
         that saved view is "selected" - which causes the unsaved action to clear. */
      if (tiles !== null) {
        return createSavedView(name, tiles, context, options);
      }
    },
    updateTileLayout,
    addNewTile,
    deleteTile,
    cloneTile,
    undoLastAction,
    updateTiles,
  };
};
